import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Power, Pencil, CalendarClock, Sun, PowerOff, Waves, UsersRound, ClipboardList, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AutopilotCard } from '@/components/Common/autopilot-card';
import { EventLogTable } from '@/components/Common/event-log-table';
import { OperationCard } from '@/components/Common/operation-card';
import { EnvironmentCard } from '@/components/autopilot/environment-card';
import { SolarCard } from '@/components/autopilot/solar-card';
import { OccupancyCard } from '@/components/autopilot/occupancy-card';
import { supabase } from '@/api/supabase';
import { ZoneContext } from '@/auth/zoneProvider';
import { useEnumMappings } from '@/contexts/enum-mappings-context';
import { useDevices } from '@/api/hooks/useDevices';
import { useZones } from '@/api/hooks/useZones';
import { useEventLog } from '@/api/hooks/useEventLog';
import { useAutopilotHook } from '@/api/hooks/useAutopilot';
import { App, Spin, DatePicker } from 'antd';
import { useSolar } from '@/api/hooks/useSolar';
import { useCamera } from '@/api/hooks/useCamera';
import { Device, DevicesResponseData } from '@/api/devices/types';
import { apiMapper } from '@/utils/apiMapper';
import { Mode, Status, OpMode } from '@/types';
import dayjs from 'dayjs';
import { ProfileModal } from '@/components/autopilot/profile-modal';
import _ from 'lodash';
import Dashboard from './dashboard';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Configuration settings for easy adjustment
const CONFIG = {
  timeline: {
    width: 97, // Adjust this value to change the timeline width (percentage)
    height: 8, // Height of the timeline bar in Tailwind height units (h-6 = 1.5rem)
    minWidth: 0, // Minimum width in pixels (0 = no minimum)
    maxWidth: 100, // Maximum width in percentage (100 = full width)
  }
};

interface TempData {
  temperature: {
    current: number | undefined
    average: number | undefined
  },
  humidity: {
    current: number | undefined
    average: number | undefined
  },
}

interface OperationalData {
  averageWorking: number;
  avgToday: number;
  devices: Array<{
    id: string
    deviceName: string
    status: Status
    isAlarm: boolean
    isOnline: boolean
    opMode: OpMode
    mode: Mode
  }>
  units: Array<{
    status?: 'on' | 'off' | 'alarm';
    type?: 'cool' | 'fan' | 'dry';
    count: number;
  }>;
}

interface AutopilotData {
  id: string;
  user?: string;
  status: string;
  automation: string;
  stage: string;
  event: string;
  currentGear?: {
    id: number;
    gear: string;
    config: {
      pmv_offset: number;
      description: string;
    };
  };
}

interface SolarData {
  ratio: number;
  solarSurplus: number;
  irradiance: number;
}

interface CameraData {
  current: number;
  peopleIn: number;
  peopleOut: number;
}

interface StageLogData {
  stage: string;
  startedAt: string;
  endedAt: string | null;
  working: number;
  areaTemp: number;
  setTemp: number;
  returnTemp: number;
  occupancy: number;
  solar: number;
  duration: number;
  shadow?: boolean;
}

interface EventLogData {
  time: string;
  event: string;
  name: string;
  working: number;
  areaTemp: number;
  setTemp: number;
  returnTemp: number;
  occupancy: number;
  solar: number;
  shadow?: boolean;
  stages?: StageLogData[];
  isExpanded?: boolean;
}

// Type for the grouped automation logs API response
interface GroupedAutomationLog {
  automation_id: number | null;
  automation_name: string;
  profile_id: number | null;
  profile_name: string;
  blueprint_name: string;
  shadow: boolean;
  started_at: string;
  ended_at: string;
  stages: {
    stage: string;
    started_at: string;
    ended_at: string;
    shadow: boolean;
    metadata: any | null;
  }[];
  metadata: any | null;
}

interface Profile {
  id?: number;
  name?: string;
  automation_profiles?: {
    automation: {
      id: number;
      name: string;
    };
    start_time: string;
    end_time: string;
  }[];
}

const mappingOperationalData = (systemData?: DevicesResponseData, prev?: OperationalData): OperationalData => {
  // Log the incoming data to help debug
  console.log('mappingOperationalData called with:', {
    systemDataAvgWorking: systemData?.latest_data?.avg_working_percentage?.value,
    prevAvgWorking: prev?.averageWorking
  });

  return ({
    // Prioritize the API data over the previous value to ensure we always have the latest
    averageWorking: systemData?.latest_data?.avg_working_percentage?.value ?? prev?.averageWorking ?? 0,
    avgToday: prev?.avgToday ?? 0,
    devices: systemData?.devices?.map((device: Device) => {
      // Handle mode definition which might be capitalized
      let mode: Mode;
      if (device?.latest_data?.mode_read?.definition) {
        // Ensure mode values are lowercase to handle capitalized enum values from API
        mode = device.latest_data.mode_read.definition.toLowerCase() as Mode;
      } else {
        // Fallback to using the numeric value with apiMapper, but don't default to 'cool'
        // Use the value directly if no mapping found
        const modeValue = device?.latest_data?.mode_read?.value;
        mode = modeValue !== undefined && modeValue !== null
          ? (apiMapper.mode_read[String(modeValue)] ?? String(modeValue)) as Mode
          : 'cool';
      }

      return {
        id: device.device_id,
        deviceName: device.name,
        status: apiMapper.status_read[device?.latest_data?.status_read?.value ?? 0] ?? 'off',
        isAlarm: Boolean(device?.latest_data?.alarm?.value),
        isOnline: device?.latest_data?.online_status?.value === 1 || Boolean(device?.latest_data?.online_status?.value),
        opMode: apiMapper.operation_mode_read[device?.latest_data?.operation_mode_read?.value ?? 0] ?? 'local',
        mode,
      };
    }) ?? [],
    units: [
      {
        status: 'on',
        count: systemData?.devices?.filter(device => device.latest_data?.status_read?.value === 1)?.length ?? 0,
      },
      {
        status: 'off',
        // Only count devices as 'off' if they are online but turned off
        count: systemData?.devices?.filter(device => {
          const isOff = device.latest_data?.status_read?.value === 0;
          const isOnline = device.latest_data?.online_status?.value === 1 || 
                           Boolean(device.latest_data?.online_status?.value);
          return isOff && isOnline;
        })?.length ?? 0,
      },
      {
        status: 'alarm',
        count: systemData?.devices?.filter(device => device.latest_data?.alarm?.value === 1)?.length ?? 0,
      },
      {
        type: 'cool',
        count: systemData?.devices?.filter(device => {
          const isOn = device.latest_data?.status_read?.value === 1;
          const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
          return isOn && mode === 'cool';
        })?.length ?? 0,
      },
      {
        type: 'fan',
        count: systemData?.devices?.filter(device => {
          const isOn = device.latest_data?.status_read?.value === 1;
          const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
          return isOn && mode === 'fan';
        })?.length ?? 0,
      },
      {
        type: 'dry',
        count: systemData?.devices?.filter(device => {
          const isOn = device.latest_data?.status_read?.value === 1;
          const mode = device.latest_data?.mode_read?.definition?.toLowerCase();
          return isOn && mode === 'dry';
        })?.length ?? 0,
      }
    ]
  });
};

const updateTempData = (prevData: TempData | undefined, datapoint: string, value: number): TempData => {
  if (!prevData) {
    return {
      temperature: {
        current: undefined,
        average: undefined,
      },
      humidity: {
        current: undefined,
        average: undefined,
      },
    };
  }

  const newData = { ...prevData };

  switch (datapoint) {
    case 'avg_room_temperature':
      newData.temperature.current = value;
      break;
    case 'avg_relative_humidity':
      newData.humidity.current = value;
      break;
    case 'today_avg_relative_humidity':
      newData.humidity.average = value;
      break;
    case 'today_avg_room_temperature':
      newData.temperature.average = value;
      break;
  }

  return newData;
};

const timelineData = [
  { time: '00:00' }, { time: '01:00' }, { time: '02:00' }, { time: '03:00' },
  { time: '04:00' }, { time: '05:00' }, { time: '06:00' }, { time: '07:00' },
  { time: '08:00' }, { time: '09:00' }, { time: '10:00' }, { time: '11:00' },
  { time: '12:00' }, { time: '13:00' }, { time: '14:00' }, { time: '15:00' },
  { time: '16:00' }, { time: '17:00' }, { time: '18:00' }, { time: '19:00' },
  { time: '20:00' }, { time: '21:00' }, { time: '22:00' }, { time: '23:00' }
];

const eventTypes = [
  { id: 'none', label: 'None', name: '', color: 'bg-light-grey' },
  { id: 'softStart', label: 'Soft Start', name: 'Softstart', color: 'bg-mid-blue', icon: <Power className="h-2 w-2 text-white" /> },
  { id: 'asp', label: 'ASP', name: 'ASP', color: 'bg-mid-orange', icon: <Sun className="h-2 w-2 text-white" /> },
  { id: 'flowSave', label: 'Flow Save', name: 'Flowsave', color: 'bg-green', icon: <Waves className="h-2 w-2 text-white" /> },
  { id: 'softClose', label: 'Soft Close', name: 'Softclose', color: 'bg-[#5E5E5E]', icon: <PowerOff className="h-2 w-2 text-white" /> },
  { id: 'apl', label: 'APL', name: 'APL', color: 'bg-purple', icon: <UsersRound className="h-2 w-2 text-white" /> },
];

export function AutopilotPage() {
  const [tempData, setTempData] = useState<TempData>();
  const [operationalData, setOperationalData] = useState<OperationalData>()
  const [autopilotData, setAutopilotData] = useState<AutopilotData>();
  const [solarData, setSolarData] = useState<SolarData>()
  const [cameraData, setCameraData] = useState<CameraData>()
  const [eventLogData, setEventLogData] = useState<EventLogData[]>([])
  const [isVisibleProfileModal, setIsVisibleProfileModal] = useState(false)
  const [profile, setProfile] = useState<Profile>({})

  // The toggle expand functionality is now handled in the EventLogTable component
  const { message } = App.useApp();
  const zoneContext = useContext(ZoneContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize selectedDate from URL parameter or default to today
  const dateFromUrl = searchParams.get('date');
  const initialDate = dateFromUrl ? dayjs(dateFromUrl) : dayjs();
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(initialDate);

  // Update URL when date changes
  const handleDateChange = (newDate: dayjs.Dayjs) => {
    setSelectedDate(newDate);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('date', newDate.format('YYYY-MM-DD'));
    setSearchParams(newSearchParams);
  };

  // Sync date from URL when navigating back/forward
  useEffect(() => {
    const dateFromUrl = searchParams.get('date');
    if (dateFromUrl && dayjs(dateFromUrl).isValid()) {
      const urlDate = dayjs(dateFromUrl);
      if (!urlDate.isSame(selectedDate, 'day')) {
        setSelectedDate(urlDate);
      }
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: systemData, isLoading, refetch: systemRefetch } = useDevices({ expand: 'latest_data,devices' });
  const { data: zoneData, isLoading: zoneLoading, refetch: zoneRefetch } = useZones({ expand: 'latest_data' }, zoneContext.id || '');
  const { data: autopilotsData, isLoading: autopilotLoading, refetch: autoPilotRefetch } = useAutopilotHook({ level: 'building' });
  const { data: solarApiData, isLoading: solarLoading, refetch: solarRefetch } = useSolar({ expand: 'latest_data' });
  const { data: cameraApiData, isLoading: cameraLoading, refetch: cameraRefetch } = useCamera({ expand: 'latest_data' });
  const { data: eventLogApiData, isLoading: eventLogLoading, refetch: eventLogRefetch } = useEventLog({
    date: selectedDate.format('YYYY-MM-DD'),
    expand: 'stages'
  });

  // Add keyboard shortcuts for date navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger if no input is focused
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      if (e.key === 'ArrowLeft') {
        const prevDay = selectedDate.subtract(1, 'day');
        handleDateChange(prevDay);
        setTimeout(() => {
          eventLogRefetch();
        }, 0);
      } else if (e.key === 'ArrowRight') {
        const nextDay = selectedDate.add(1, 'day');
        handleDateChange(nextDay);
        setTimeout(() => {
          eventLogRefetch();
        }, 0);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedDate, handleDateChange, eventLogRefetch]);

  // Get enum mappings context
  const { enumMappings, updateEnumMappings: updateEnumMappingsContext } = useEnumMappings();

  // Use callback to avoid recreating this function on every render
  const memoizedUpdateEnumMappings = useCallback((device: Device) => {
    updateEnumMappingsContext(device);
  }, [updateEnumMappingsContext]);

  useEffect(() => {
    const subscription = supabase.channel('table data')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'latest_data',
          filter: 'site_id=eq.alto-cero-dev',
        },
        (payload) => {
          if (!payload.new) return;
          const { device_id, datapoint, value } = payload.new;
          if ([
            'avg_room_temperature',
            'avg_relative_humidity',
            'today_avg_relative_humidity',
            'today_avg_relative_humidity',
          ].includes(datapoint)) {
            setTempData(prev => updateTempData(prev, datapoint, value));
          } else if (device_id === 'ac_subsystem' && ['avg_working_percentage'].includes(datapoint)) {
            console.log('Real-time update for avg_working_percentage:', value);
            setOperationalData(prev => {
              if (!prev) return prev;
              const newData = {
                ...prev,
                averageWorking: value,
              };
              console.log('Updated averageWorking via real-time:', {
                oldValue: prev.averageWorking,
                newValue: value
              });
              return newData;
            });
          } else if (device_id === zoneContext.id && ['avg_temperature_setpoint'].includes(datapoint)) {
            setOperationalData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                avgToday: value,
              };
            });
          } else if (device_id === 'solar_subsystem' && ['solar_surplus_rate', 'solar_surplus', 'irradiance'].includes(datapoint)) {
            setSolarData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [apiMapper[datapoint].solarKey]: value,
              };
            });
          } else if (device_id === 'camera_subsystem' && ['occupancy_current', 'today_in_count', 'today_out_count', 'in_count_daily', 'out_count_daily'].includes(datapoint)) {
            setCameraData(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                [apiMapper[datapoint].cameraKey]: value,
              };
            });
          } else if (operationalData?.devices?.filter(v => v.id === device_id)
            && ['status_read', 'alarm', 'mode_read'].includes(datapoint)) {
            setOperationalData((prev) => {
              if (!prev) return prev;
              const newDevices = prev.devices.map(item => {
                if (item.id !== device_id) return item;

                // Handle mode_read with definition (which might be capitalized)
                if (datapoint === 'mode_read') {
                  if (payload.new.definition) {
                    return {
                      ...item,
                      // Ensure mode values are lowercase to handle capitalized enum values from API
                      [apiMapper[datapoint].tableKey]: payload.new.definition.toLowerCase(),
                    };
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use apiMapper
                    return {
                      ...item,
                      // Preserve existing mode value if no mapping found
                      [apiMapper[datapoint].tableKey]: apiMapper[datapoint][value as keyof typeof apiMapper.mode_read] ?? item.mode ?? value,
                    };
                  }
                } else if (datapoint === 'status_read') {
                  if (payload.new.definition) {
                    // If definition is available, use it (handle capitalization)
                    const statusValue = payload.new.definition.toLowerCase();
                    return {
                      ...item,
                      [apiMapper[datapoint].tableKey]: statusValue,
                    };
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, try enum mappings first
                    const statusValue = String(value); // Convert to string for object key lookup
                    const mappedStatus = enumMappings.status[statusValue];
                    if (mappedStatus) {
                      return {
                        ...item,
                        [apiMapper[datapoint].tableKey]: mappedStatus.toLowerCase(),
                      };
                    } else {
                      // Fall back to apiMapper or direct mapping
                      const statusFromMapper = apiMapper[datapoint][value as keyof typeof apiMapper.status_read];
                      if (statusFromMapper) {
                        return {
                          ...item,
                          [apiMapper[datapoint].tableKey]: statusFromMapper,
                        };
                      } else {
                        // Direct mapping for common values
                        return {
                          ...item,
                          [apiMapper[datapoint].tableKey]: value === 1 || value === true || value === '1' ? 'on' : 'off',
                        };
                      }
                    }
                  }
                } else if (datapoint === 'online_status') {
                  // For online_status, convert the numeric value to boolean
                  return {
                    ...item,
                    isOnline: value === 1 || value === true,
                  };
                } else if (datapoint === 'operation_mode_read') {
                  if (payload.new.definition) {
                    // If definition is available, use it
                    return {
                      ...item,
                      opMode: payload.new.definition,
                    };
                  } else if (value !== undefined && value !== null) {
                    // If no definition but we have a value, use apiMapper
                    return {
                      ...item,
                      opMode: apiMapper.operation_mode_read[value as keyof typeof apiMapper.operation_mode_read] ?? value,
                    };
                  }
                }

                return {
                  ...item,
                  [apiMapper[datapoint].tableKey]: apiMapper[datapoint][value as any] ?? value,
                };
              })
              return {
                ...prev,
                devices: newDevices,
                units: [
                  {
                    status: 'on',
                    count: newDevices.filter(item => item.status === 'on').length || 0,
                  },
                  {
                    status: 'off',
                    count: newDevices.filter(item => item.status === 'off').length || 0,
                  },
                  {
                    status: 'alarm',
                    count: newDevices.filter(item => item.isAlarm).length || 0,
                  },
                  {
                    type: 'cool',
                    count: newDevices.filter(item => item.status === 'on' && item.mode === 'cool').length || 0,
                  },
                  {
                    type: 'fan',
                    count: newDevices.filter(item => item.status === 'on' && item.mode === 'fan').length || 0,
                  },
                  {
                    type: 'dry',
                    count: newDevices.filter(item => item.status === 'on' && item.mode === 'dry').length || 0,
                  }
                ]
              }
            })
          }
        }
      )
      .subscribe();

    const autoPilotSubscription = supabase.channel('table data')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'autopilot',
        },
        async (payload) => {
          if (!payload.new) return;

          const { device_id, autopilot_status, updated_by_id } = payload.new;

          // Get username from auth_user table using updated_by_id
          let username = undefined;
          if (updated_by_id) {
            const { data: userData, error } = await supabase
              .from('auth_user')
              .select('username')
              .eq('id', updated_by_id)
              .single();

            if (!error && userData) {
              username = userData.username;
            }
          }

          if (device_id === 'alto-cero-dev') {
            if (autopilotData) setAutopilotData(prev => ({
              ...prev,
              status: autopilot_status,
              user: username,
            }))
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      autoPilotSubscription.unsubscribe()
    };
  }, [memoizedUpdateEnumMappings, enumMappings]);

  useEffect(() => {
    systemRefetch()
    autoPilotRefetch()
    zoneRefetch()
    solarRefetch()
    cameraRefetch()
    eventLogRefetch()
  }, [navigate])

  useEffect(() => {
    zoneRefetch()
  }, [zoneContext?.id])

  useEffect(() => {
    if (!autopilotsData?.[0]) return;

    const autopilot = autopilotsData[0];
    setAutopilotData({
      id: String(autopilot.id),
      user: autopilot.updated_by?.username ?? undefined,
      status: autopilot.autopilot_status ?? 'off',
      automation: autopilot.current_automation ?? '',
      stage: autopilot.current_automation?.split(' ')?.[1] ?? '',
      event: autopilot.current_automation?.name ?? '',
      currentGear: autopilot.current_gear ?? undefined,
    });
  }, [autopilotsData]);

  // Update enum mappings when system data is loaded
  useEffect(() => {
    if (systemData?.devices && systemData.devices.length > 0) {
      // Only update mappings if they're empty or if the system data significantly changed
      const hasNoMappings = Object.values(enumMappings.mode).length === 0 ||
        Object.values(enumMappings.status).length === 0;

      if (hasNoMappings) {
        console.log('Initializing enum mappings from system data in autopilot page');
        // Update mappings from all devices to ensure we have complete mappings
        systemData.devices.forEach(device => {
          memoizedUpdateEnumMappings(device);
        });
      }
    }
  }, [systemData?.devices, memoizedUpdateEnumMappings, enumMappings.mode, enumMappings.status]);

  useEffect(() => {
    // Log the system data to help debug
    console.log('systemData changed:', {
      hasData: !!systemData,
      avgWorkingPercentage: systemData?.latest_data?.avg_working_percentage?.value
    });

    setOperationalData((prev) => {
      const newData = mappingOperationalData(systemData, prev);
      console.log('Updated operational data:', {
        prevAverageWorking: prev?.averageWorking,
        newAverageWorking: newData.averageWorking
      });
      return newData;
    });
  }, [systemData]);

  useEffect(() => {
    if (!zoneData || !zoneData.latest_data) return;

    setTempData({
      temperature: {
        current: zoneData.latest_data?.avg_room_temperature?.value ?? 0,
        average: zoneData.latest_data?.today_avg_room_temperature?.value ?? 0,
      },
      humidity: {
        current: zoneData.latest_data?.avg_relative_humidity?.value ?? 0,
        average: zoneData.latest_data?.today_avg_relative_humidity?.value ?? 0,
      },
    });

    setOperationalData((prev) => ({
      ...prev,
      avgToday: zoneData.latest_data?.avg_temperature_setpoint?.value ?? 0,
    }));
  }, [zoneData]);

  useEffect(() => {
    if (!solarApiData?.latest_data) return;
    setSolarData({
      ratio: solarApiData.latest_data?.solar_surplus_rate?.value ?? 0,
      solarSurplus: solarApiData.latest_data?.solar_surplus?.value ?? 0,
      irradiance: solarApiData.latest_data?.irradiance?.value ?? 0,
    })
  }, [solarApiData]);

  useEffect(() => {
    if (!cameraApiData?.latest_data) return;
    setCameraData({
      current: cameraApiData.latest_data?.occupancy_current?.value ?? 0,
      // Check for both old and new field names for people in/out
      peopleIn: cameraApiData.latest_data?.in_count_daily?.value ??
        cameraApiData.latest_data?.today_in_count?.value ?? 0,
      peopleOut: cameraApiData.latest_data?.out_count_daily?.value ??
        cameraApiData.latest_data?.today_out_count?.value ?? 0,
    })
  }, [cameraApiData]);

  useEffect(() => {
    if (!eventLogApiData) return;

    try {
      // Check if data exists and is an array
      if (Array.isArray(eventLogApiData?.data)) {
        setEventLogData(eventLogApiData.data.map(event => {
          // Process stages if they exist
          const stages = event.stages?.map(stage => ({
            stage: stage.stage,
            startedAt: stage.started_at,
            endedAt: stage.ended_at,
            working: stage.metadata?.avg_working_percentage || 0,
            areaTemp: stage.metadata?.avg_room_temperature || 0,
            setTemp: stage.metadata?.avg_temperature_setpoint || 0,
            returnTemp: stage.metadata?.avg_return_temperature || 0,
            occupancy: stage.metadata?.occupancy_current || 0,
            solar: stage.metadata?.solar_surplus_rate || 0,
            duration: stage.metadata?.stage_duration_minutes || 0,
            shadow: stage.shadow || false
          })) || [];

          // Find the stage with the latest end time (or ongoing stage)
          let latestEndTime = null;
          let isOngoing = false;
          
          stages.forEach(stage => {
            if (!stage.endedAt) {
              // If any stage has no end time, the event is ongoing
              isOngoing = true;
            } else if (!latestEndTime || new Date(stage.endedAt) > new Date(latestEndTime)) {
              latestEndTime = stage.endedAt;
            }
          });
          
          return {
            time: latestEndTime ? dayjs(latestEndTime).format('HH:mm') : isOngoing ? 'Now' : dayjs(event.started_at).format('HH:mm'),
            event: event.blueprint_name || '',
            name: event.automation_name || '',
            working: event.metadata?.avg_working_percentage || 0,
            areaTemp: event.metadata?.avg_room_temperature || 0,
            setTemp: event.metadata?.avg_temperature_setpoint || 0,
            returnTemp: event.metadata?.avg_return_temperature || 0,
            occupancy: event.metadata?.occupancy_current || 0,
            solar: event.metadata?.solar_surplus_rate || 0,
            shadow: event.shadow || false,
            stages: stages,
            isExpanded: false, // This is now managed by the EventLogTable component
            isOngoing: isOngoing
          };
        }));
      } else {
        // If data is not an array, set empty array
        setEventLogData([]);
      }

      // Safely set profile data
      setProfile({
        ...((eventLogApiData?.metadata?.profile) || {}),
        name: eventLogApiData?.metadata?.profile_name || '',
      });
    } catch (error) {
      console.error('Error processing event log data:', error);
      // Set empty data on error
      setEventLogData([]);
      setProfile({});
    }
  }, [eventLogApiData]);

  // Only show loading spinner for the main data, not for event log data
  if (isLoading || zoneLoading || autopilotLoading || solarLoading || cameraLoading
    || !tempData || !autopilotData || !operationalData || !solarData || !cameraData) {
    return (
      <div className="space-y-6 p-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-center">
          <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-48">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
          </div>
          <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-6 w-32">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
          </div>
        </div>
        
        {/* Cards grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="p-4 bg-white rounded-lg border space-y-3">
              <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-4 w-2/3">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
              <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-8 w-full">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
              <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-3 w-1/2">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Chart skeleton */}
        <div className="grid grid-cols-4 gap-6">
          <div className="col-span-3 p-6 bg-white rounded-lg border">
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-6 w-1/3 mb-4">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-64 w-full">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
          </div>
          
          {/* Event log skeleton */}
          <div className="p-4 bg-white rounded-lg border space-y-3">
            <div className="relative overflow-hidden rounded-md bg-gray-200/40 h-5 w-2/3">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
            </div>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="relative overflow-hidden rounded-md bg-gray-200/40 h-4 w-full">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-skeleton-shimmer"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // If event log is loading, we'll still render the page but show a loading state for the event log section

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 flex flex-col gap-4">
      {/* Stats Grid */}
      {/* Autopilot Status   */}
      <div className='flex gap-3'>
        <div className='w-[283px] min-w-[283px]'>
          <AutopilotCard
            id={autopilotData?.id}
            status={autopilotData?.status}
            isShowEvent={true}
            stage={autopilotData?.stage}
            event={autopilotData?.event}
            showGear={true}
            currentGear={autopilotData?.currentGear}
          />
        </div>
        <div className='grow min-w-[330px]'>
          <OperationCard
            units={operationalData?.units}
            info={{
              averageWorking: operationalData?.averageWorking,
              avgToday: operationalData?.avgToday,
            }}
          />
        </div>
        <div className="grid grid-cols-3 gap-3 grow">
          <EnvironmentCard
            temperature={tempData?.temperature}
            humidity={tempData?.humidity}
          />
          <SolarCard
            ratio={solarData?.ratio}
            solarSurplus={solarData?.solarSurplus}
            irradiance={solarData?.irradiance}
          />
          <OccupancyCard
            current={cameraData?.current}
            peopleIn={cameraData?.peopleIn}
            peopleOut={cameraData?.peopleOut}
          />
        </div>
      </div>

      {/* Event Timeline and Charts Section */}

      <div className="flex gap-4 flex-shrink-0">
        {/* Graph Section */}
        <div className="flex-[7] flex flex-col gap-4">
          {/* Dashboard Container - Now includes Event Timeline */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-light-grey overflow-hidden">
            <Dashboard 
              key={selectedDate.format('YYYY-MM-DD')} 
              selectedDate={selectedDate}
              eventTypes={eventTypes}
              profileName={profile?.name}
              eventLogData={(eventLogApiData as any)?.data || []}
              eventLogLoading={eventLogLoading}
            />
          </div>
        </div>

        {/* Event Log */}
        <div className="flex-[5] flex flex-col gap-1">
          <div className="flex items-center justify-between mb-1 flex-shrink-0">
            <div className="flex items-center gap-1">
              <div className="h-[18px] w-[18px] bg-light-grey rounded flex items-center justify-center">
                <ClipboardList className="h-3 w-3 text-primary" />
              </div>
              <h3 className="text-dark-blue heading-7">Event Log</h3>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Previous Day Button */}
              <button
                onClick={() => {
                  const prevDay = selectedDate.subtract(1, 'day');
                  handleDateChange(prevDay);
                  setTimeout(() => {
                    eventLogRefetch();
                  }, 0);
                }}
                className="h-7 w-7 flex items-center justify-center border border-light-grey hover:border-primary rounded-md transition-colors"
                title="Previous day (←)"
              >
                <ChevronLeft className="h-3 w-3 text-primary" />
              </button>

              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  if (date) {
                    handleDateChange(date);
                    // Only refetch the event log data, not the entire page
                    setTimeout(() => {
                      eventLogRefetch();
                    }, 0);
                  }
                }}
                allowClear={false}
                format="YYYY-MM-DD"
                size="small"
                placeholder="Select date"
                className="w-32 border border-light-grey hover:border-primary focus:border-primary rounded-md text-xs"
                style={{ height: '28px' }}
                suffixIcon={<CalendarClock className="h-3 w-3 text-primary" />}
              />

              {/* Next Day Button */}
              <button
                onClick={() => {
                  const nextDay = selectedDate.add(1, 'day');
                  handleDateChange(nextDay);
                  setTimeout(() => {
                    eventLogRefetch();
                  }, 0);
                }}
                className="h-7 w-7 flex items-center justify-center border border-light-grey hover:border-primary rounded-md transition-colors"
                title="Next day (→)"
              >
                <ChevronRight className="h-3 w-3 text-primary" />
              </button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                onClick={() => {
                  const today = dayjs();
                  handleDateChange(today);
                  // Only refetch the event log data, not the entire page
                  setTimeout(() => {
                    eventLogRefetch();
                  }, 0);
                }}
              >
                <p className='button-xs'>Today</p>
              </Button>
              <Button disabled={_.isNil(profile?.id)} variant="outline" size="sm" className="text-xs border-primary text-primary" onClick={() => setIsVisibleProfileModal(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                <p className='button-xs'>Edit Profile</p>
              </Button>
            </div>
          </div>

          {/* Use the reusable EventLogTable component */}
          <div className="flex flex-col min-h-[400px]">
            <EventLogTable
              eventLogData={eventLogData}
              eventTypes={eventTypes}
              isLoading={eventLogLoading}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onRefetch={eventLogRefetch}
              showDataColumns={true}
            />
          </div>
        </div>
      </div>
      {isVisibleProfileModal && (
        <ProfileModal
          isOpen={true}
          onClose={() => setIsVisibleProfileModal(false)}
          message={message}
          edit={true}
          data={profile}
          refetch={systemRefetch}
        />
      )}
      </div>
    </div>
  );
}